import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const MAROON = "var(--user-maroon, #2B0000)";
const MAROON_DARK = "var(--user-maroon-deep, #160002)";
const GOLD = "var(--user-gold, #D6B33F)";
const GOLD_SOFT = "var(--user-gold-soft, #E8C96A)";
const CREAM = "var(--user-cream, #F8E8D2)";
const SECTION_BG = "var(--user-bg, #E8D9D5)";

export default function HomeFinalCta() {
  const sectionRef = useRef(null);
  const bannerRef = useRef(null);
  const contentRef = useRef(null);
  const imageCircleRef = useRef(null);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const banner = bannerRef.current;
    const content = contentRef.current;
    const imageCircle = imageCircleRef.current;
    const image = imageRef.current;
    const button = buttonRef.current;

    if (
      !section ||
      !banner ||
      !content ||
      !imageCircle ||
      !image ||
      !button
    ) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      gsap.set([banner, content, imageCircle, image, button], {
        x: 0,
        y: 0,
        scale: 1,
        autoAlpha: 1,
        clearProps: "transform",
      });

      return undefined;
    }

    const context = gsap.context(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: {
          ease: "power3.out",
        },
      });

      timeline
        .fromTo(
          banner,
          {
            y: 45,
            scale: 0.97,
            autoAlpha: 0,
          },
          {
            y: 0,
            scale: 1,
            autoAlpha: 1,
            duration: 0.75,
            overwrite: true,
          }
        )
        .fromTo(
          content,
          {
            x: -35,
            y: 20,
            autoAlpha: 0,
          },
          {
            x: 0,
            y: 0,
            autoAlpha: 1,
            duration: 0.7,
            overwrite: true,
          },
          0.12
        )
        .fromTo(
          imageCircle,
          {
            x: 45,
            scale: 0.88,
            autoAlpha: 0,
          },
          {
            x: 0,
            scale: 1,
            autoAlpha: 1,
            duration: 0.8,
            overwrite: true,
          },
          0.18
        )
        .fromTo(
          image,
          {
            scale: 1.14,
          },
          {
            scale: 1,
            duration: 1,
            ease: "power2.out",
            overwrite: true,
          },
          0.18
        )
        .fromTo(
          button,
          {
            x: 28,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
            duration: 0.6,
            overwrite: true,
          },
          0.3
        );

      const topToBottomTrigger = ScrollTrigger.create({
        trigger: section,
        start: "top 78%",
        invalidateOnRefresh: true,

        onEnter: () => {
          timeline.restart();
        },
      });

      const bottomToTopTrigger = ScrollTrigger.create({
        trigger: section,
        start: "bottom 92%",
        invalidateOnRefresh: true,

        onEnterBack: () => {
          timeline.restart();
        },
      });

      const handleMouseEnter = () => {
        gsap.to(banner, {
          y: -5,
          duration: 0.35,
          ease: "power3.out",
          overwrite: "auto",
        });

        gsap.to(image, {
          scale: 1.05,
          duration: 0.7,
          ease: "power3.out",
          overwrite: "auto",
        });
      };

      const handleMouseLeave = () => {
        gsap.to(banner, {
          y: 0,
          duration: 0.45,
          ease: "power3.out",
          overwrite: "auto",
        });

        gsap.to(image, {
          scale: 1,
          duration: 0.75,
          ease: "power3.out",
          overwrite: "auto",
        });
      };

      banner.addEventListener("mouseenter", handleMouseEnter);
      banner.addEventListener("mouseleave", handleMouseLeave);

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 300);

      const handleWindowLoad = () => {
        ScrollTrigger.refresh();
      };

      window.addEventListener("load", handleWindowLoad);

      return () => {
        window.clearTimeout(refreshTimer);
        window.removeEventListener("load", handleWindowLoad);

        banner.removeEventListener("mouseenter", handleMouseEnter);
        banner.removeEventListener("mouseleave", handleMouseLeave);

        topToBottomTrigger.kill();
        bottomToTopTrigger.kill();
        timeline.kill();
      };
    }, section);

    return () => {
      context.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden px-4 py-10 sm:px-8 lg:px-14"
      style={{
        backgroundColor: SECTION_BG,
      }}
    >
      <div
        ref={bannerRef}
        className="
          relative
          mx-auto
          max-w-[1320px]
          overflow-hidden
          rounded-[28px]
          border
          px-5
          py-7
          shadow-[0_24px_65px_rgba(43,0,0,0.22)]
          will-change-transform

          sm:px-7
          sm:py-8

          lg:min-h-[215px]
          lg:px-10
          lg:py-7
        "
        style={{
          borderColor: "rgba(214,179,63,0.32)",
          backgroundColor: MAROON,
        }}
      >
        {/* Main background */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: `
              radial-gradient(
                circle at 74% 30%,
                rgba(214,179,63,0.18),
                transparent 30%
              ),
              linear-gradient(
                100deg,
                ${MAROON_DARK} 0%,
                ${MAROON} 52%,
                #430306 100%
              )
            `,
          }}
        />

        {/* Decorative glow */}
        <div
          className="
            pointer-events-none
            absolute
            -left-24
            top-1/2
            h-52
            w-52
            -translate-y-1/2
            rounded-full
            blur-3xl
          "
          style={{
            backgroundColor: "rgba(214,179,63,0.08)",
          }}
        />

        {/* Desktop jewellery image circle */}
        <div
          ref={imageCircleRef}
          className="
            pointer-events-none
            absolute
            -right-5
            -top-14
            hidden
            h-[310px]
            w-[390px]
            overflow-hidden
            rounded-[50%]
            border
            will-change-transform

            lg:block
          "
          style={{
            borderColor: "rgba(214,179,63,0.28)",
            backgroundColor: "#3C070A",
            boxShadow:
              "inset 0 0 50px rgba(0,0,0,0.35), 0 0 50px rgba(0,0,0,0.18)",
          }}
        >
          <img
            ref={imageRef}
            src="https://images.unsplash.com/photo-1599643477877-530eb83abc8e?q=85&w=900&auto=format&fit=crop"
            alt="Gold jewellery necklace"
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover"
          />

          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(43,0,0,0.45), transparent 45%, rgba(43,0,0,0.1))",
            }}
          />
        </div>

        {/* Decorative circle outline */}
        <div
          className="
            pointer-events-none
            absolute
            -right-8
            -top-16
            hidden
            h-[325px]
            w-[405px]
            rounded-[50%]
            border

            lg:block
          "
          style={{
            borderColor: "rgba(214,179,63,0.15)",
          }}
        />

        <div
          className="
            relative
            z-10
            flex
            flex-col
            gap-6

            lg:min-h-[160px]
            lg:justify-center
            lg:pr-[355px]
          "
        >
          {/* Content */}
          <div
            ref={contentRef}
            className="max-w-[760px] will-change-transform"
          >
            <p
              className="
                text-[13px]
                font-semibold
                uppercase
                tracking-[0.18em]

                sm:text-[15px]
                lg:text-[17px]
              "
              style={{
                color: GOLD,
              }}
            >
              Timeless Elegance
            </p>

            <h2
              className="
                mt-2
                font-serif
                text-[25px]
                font-semibold
                leading-[1.18]

                sm:text-[31px]
                lg:text-[33px]
              "
              style={{
                color: CREAM,
              }}
            >
              Discover handcrafted jewellery designed to celebrate every
              moment. ✨
            </h2>

            <p
              className="
                mt-3
                max-w-[680px]
                text-[13px]
                leading-6

                sm:text-[15px]
                sm:leading-7

                lg:text-[16px]
              "
              style={{
                color: "rgba(248,232,210,0.76)",
              }}
            >
              Explore our exclusive collections crafted with unmatched quality,
              elegance, and timeless beauty.
            </p>
          </div>

       
        </div>

        {/* Mobile jewellery image */}
        <div
          className="
            relative
            z-10
            mt-6
            h-[190px]
            overflow-hidden
            rounded-[20px]
            border

            sm:h-[230px]

            lg:hidden
          "
          style={{
            borderColor: "rgba(214,179,63,0.22)",
            backgroundColor: "#3C070A",
          }}
        >
          <img
            src="https://images.unsplash.com/photo-1599643477877-530eb83abc8e?q=85&w=900&auto=format&fit=crop"
            alt="Gold jewellery necklace"
            loading="eager"
            decoding="async"
            className="h-full w-full object-cover"
          />

          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, transparent 45%, rgba(43,0,0,0.35))",
            }}
          />

          <div className="pointer-events-none absolute inset-[7px] rounded-[15px] border border-white/10" />
        </div>

        {/* Bottom highlight */}
        <div
          className="pointer-events-none absolute inset-x-16 bottom-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(214,179,63,0.65), transparent)",
          }}
        />
      </div>
    </section>
  );
}
